import { Settings, SettingValue } from "@/shared-models/consumer/ISetting";
import { ApiService } from "@/shared-models/consumer/IApiService";
import { AuthenticationHelper } from "@/shared-models/consumer/IAuthHelper";
import DatadogLogger from "@/shared-helpers/datadog-logger.helper";
import { LogType } from "@/types/dataDogLogType";
import { SettingsCategory } from "@/shared-models/consumer/SettingsCategory";

export class ConsumerSettingsService {
  private readonly apiService: ApiService;
  private readonly authHelper: AuthenticationHelper;
  private readonly logger: typeof DatadogLogger;
  private readonly categoryKey: SettingsCategory | undefined;
  private settings: Settings = {};

  constructor(
    apiService: ApiService,
    authHelper: AuthenticationHelper,
    logger: typeof DatadogLogger = DatadogLogger,
    categoryKey?: SettingsCategory
  ) {
    this.apiService = apiService;
    this.authHelper = authHelper;
    this.logger = logger;
    this.categoryKey = categoryKey;
  }

  public async getSettings(
    categoryKey?: SettingsCategory,
    deviceTag?: string
  ): Promise<Settings> {
    try {
      const backendSettings = await this.getSettingsFromBackend(
        categoryKey || this.categoryKey,
        deviceTag
      );
      this.settings = backendSettings;
      return backendSettings || {};
    } catch (error) {
      this.logger.Log(
        `getSettings: Unexpected error: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        { error },
        LogType.ERROR
      );
      return {};
    }
  }

  public async getSetting(
    settingName: string,
    deviceTag?: string
  ): Promise<SettingValue> {
    const nullSetting = { enabled: false } as SettingValue;
    try {
      if (this.isSettingsEmpty()) {
        this.settings = await this.getSettings(this.categoryKey, deviceTag);
      }

      if (this.isSettingsEmpty()) {
        return nullSetting;
      }

      if (
        this.settings &&
        Object.prototype.hasOwnProperty.call(this.settings, settingName)
      ) {
        return this.settings[settingName];
      }
      return nullSetting;
    } catch (error) {
      this.logger.Log(
        `getSetting: Failed to get setting: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        { error, settingName },
        LogType.ERROR
      );
      return nullSetting;
    }
  }

  private isSettingsEmpty(): boolean {
    return Object.keys(this.settings).length === 0;
  }

  private validateApiService(): void {
    if (typeof this.apiService.fetchConsumerSettings !== "function") {
      const msg =
        "fetchConsumerSettings method is not implemented in the API service";
      this.logger.Log(msg, {}, LogType.ERROR);
      throw new Error(msg);
    }
  }

  private async getSettingsFromBackend(
    categoryKey?: SettingsCategory,
    paramDeviceTag?: string
  ): Promise<Settings> {
    try {
      this.validateApiService();

      const deviceTag = paramDeviceTag || this.authHelper.getDeviceTag(); //TODO: implement PJO support
      const response = await this.apiService.fetchConsumerSettings({
        deviceTag,
        categoryKey,
      });

      if (!response?.data && !response.data.settings) {
        this.logger.Log(
          "getSettingsFromBackend: No data in response",
          { response, deviceTag },
          LogType.WARN
        );
        return {};
      }

      return response.data.settings;
    } catch (error) {
      this.logger.Log(
        `getSettingsFromBackend: Failed to get settings from backend: ${
          error instanceof Error ? error.message : "Unknown error"
        }`,
        { error },
        LogType.ERROR
      );
      return {};
    }
  }
}
